// @flow
import * as React from 'react';
import {defineMessage} from 'react-intl';
import samplePaymentIntent from '../../responses/payment_intent';
import {DefaultCards, BuildInstructions} from '../../../../components/left-col';

import type {StepsConfig} from '../../../../data/GuideTypes';

const buildInstructions = [
  {
    label: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.load-stripe.gradle.label',
        defaultMessage: 'Gradle',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.load-stripe.gradle.body',
        defaultMessage: 'Add the dependencies to your build.gradle file:',
      }),
    },
    large: true,
    command: `implementation 'com.stripe:stripe-android:15.1.0'
implementation 'com.squareup.okhttp3:okhttp:4.4.0'
implementation 'com.google.code.gson:gson:2.8.6'`,
  },
  {
    label: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.load-stripe.github.label',
        defaultMessage: 'GitHub',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.load-stripe.github.body',
        defaultMessage: 'The Stripe Android SDK is open-sourced.',
      }),
    },
    link: {
      href: 'https://github.com/stripe/stripe-android',
      label: {
        message: defineMessage({
          id:
            'docsShell.integrationBuilder.aap.android.java.steps.load-stripe.github.link.label',
          defaultMessage: 'View on GitHub',
        }),
      },
    },
  },
];

export default ([
  {
    file: 'server',
    id: 'setup',
    section: 'setup-stripe',
  },
  {
    file: 'server',
    id: 'create',
    section: 'setup-stripe',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.create.heading',
        defaultMessage: 'Create a PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.android.java.steps.create.body',
        defaultMessage: `Add an endpoint on your server that creates a <a>PaymentIntent</a> when your customer is ready to pay. A PaymentIntent tracks the customer's payment lifecycle, keeping track of any failed payment attempts and ensuring the customer can only be charged once. Return the PaymentIntent's client secret in the response to finish the payment on the client.`,
      }),
      values: {
        a: (str) => (
          <a href="/docs/api/payment_intents" target="_blank">
            {str}
          </a>
        ),
      },
    },
    response: samplePaymentIntent,
  },
  {
    id: 'load-stripe',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.load-stripe.heading',
        defaultMessage: 'Install the SDK',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.load-stripe.body',
        defaultMessage:
          'To install the SDK, add stripe-android as a dependency. This sample uses OkHttp and GSON to make requests to the server, also include those as dependencies if needed.',
      }),
    },
    Component() {
      return <BuildInstructions instructions={buildInstructions} />;
    },
  },
  {
    id: 'define-form',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.define-form.heading',
        defaultMessage: 'Define your payment form',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.define-form.body',
        defaultMessage:
          'Add an instance of Stripe’s CardInputWidget to your checkout form. This is used to collect card details and ensures that the sensitive details never touch your server.',
      }),
    },
  },
  {
    id: 'fetch-pi',
    section: 'build-checkout-page',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.fetch-pi.heading',
        defaultMessage: 'Fetch a PaymentIntent',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.android.java.steps.fetch-pi.body',
        defaultMessage:
          'Make a request to your server for a PaymentIntent as soon as the view loads. Store a reference to the client secret.',
      }),
    },
  },
  {
    id: 'collect-details',
    section: 'complete-the-payment',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.collect-details.heading',
        defaultMessage: 'Collect payment details',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.collect-details.body',
        defaultMessage:
          'When the customer clicks the pay button, create an <a>ConfirmPaymentIntentParams</a> object with the card details and client secret from the PaymentIntent you created on the server.',
      }),
      values: {
        a: (str) => (
          <a
            href="https://stripe.dev/stripe-android/stripe/com.stripe.android.model/-confirm-payment-intent-params/index.html"
            target="_blank"
          >
            {str}
          </a>
        ),
      },
    },
  },
  {
    id: 'finish',
    section: 'complete-the-payment',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.finish.heading',
        defaultMessage: 'Finish the payment',
      }),
    },
    body: {
      message: defineMessage({
        id: 'docsShell.integrationBuilder.aap.android.java.steps.finish.body',
        defaultMessage:
          'Send the payment details to Stripe with by calling <code>paymentLauncher.confirm()</code>.',
      }),
      values: {
        code: (str) => <code>{str}</code>,
      },
    },
  },
  {
    id: 'handle-response',
    section: 'complete-the-payment',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.handle-response.heading',
        defaultMessage: 'Handle the response from Stripe',
      }),
    },
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.handle-response.body',
        defaultMessage:
          'If your customer’s card is declined, the completion block is called with an error. Show that error to your customer so they can try again with a different card. If no error occurred, tell your customer the payment was successful!',
      }),
    },
  },
  {
    id: 'test-cards',
    section: 'test-the-integration',
    heading: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.test-cards.heading',
        defaultMessage: 'Make a test payment',
      }),
    },
    testing: true,
    body: {
      message: defineMessage({
        id:
          'docsShell.integrationBuilder.aap.android.java.steps.test-cards.body',
        defaultMessage:
          'Use a test card number to try your integration. These card numbers work in test mode with any CVC, postal code, and future expiry date. Stripe also has a set of <a>international test cards</a> to test specific postal code formats (e.g. only allow numerical values for U.S. zip codes).',
      }),
      values: {
        a: (str) => (
          <a href="/docs/testing#international-cards" target="_blank">
            {str}
          </a>
        ),
      },
    },
    Component: DefaultCards,
  },
]: StepsConfig);
